import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save, X, Key, CreditCard, AlertCircle,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_restaurant_setting,
  save_restaurant_setting,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

const emptyForm = () => ({ api_key: "", access_token: "" });

export default function PabblySetting() {
  const { toast } = useToast();

  const [form, setForm] = useState(emptyForm());
  const [original, setOriginal] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({ fetch: false, save: false });

  // ── Helpers ──────────────────────────────────────────────────────────────
  const hasChanges = () => JSON.stringify(form) !== JSON.stringify(original);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading((p) => ({ ...p, fetch: true }));
    const fd = new FormData();
    fd.append("platform_connect_gateway", 0);
    fd.append("platform_connect", 6);
    try {
      const res = await server_post_data(get_restaurant_setting, fd);
      if (!res.data.error) {
        const data = res.data.message.data_platform || {};
        const loaded = {
          api_key: data[0].api_key || "",
          access_token: data[0].other_token || "",
        };
        setForm(loaded);
        setOriginal(JSON.parse(JSON.stringify(loaded)));
      } else {
        toast({ title: "Error", description: res.data.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Failed to load Pabbly settings", variant: "destructive" });
    } finally {
      setLoading((p) => ({ ...p, fetch: false }));
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Field change ──────────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => { const { [field]: _, ...rest } = p; return rest; });
  };

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.api_key.trim()) errs.api_key = "API Key is required";
    if (!form.access_token.trim()) errs.access_token = "Access Token is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!validate()) {
      toast({ title: "Validation Error", description: "Fill all required fields", variant: "destructive" });
      return;
    }
    setLoading((p) => ({ ...p, save: true }));
    const fd = new FormData();
    fd.append("from_where", "8");           // adjust as per your backend
    fd.append("platform_connect", "6");
    fd.append("api_key", form.api_key);
    fd.append("access_token", form.access_token);
    try {
      const res = await server_post_data(save_restaurant_setting, fd);
      if (res.data.error) {
        toast({ title: "Error", description: res.data.message, variant: "destructive" });
      } else {
        toast({ title: "Saved", description: "Pabbly credentials saved successfully" });
        fetchData();
      }
    } catch {
      toast({ title: "Error", description: "Failed to save Pabbly settings", variant: "destructive" });
    } finally {
      setLoading((p) => ({ ...p, save: false }));
    }
  }, [form, toast, fetchData]);

  // ── Fields config ─────────────────────────────────────────────────────────
  const FIELDS = [
    { key: "api_key", label: "API Key", placeholder: "Enter Pabbly API Key", secret: false, icon: CreditCard },
    { key: "access_token", label: "End Point", placeholder: "Enter Pabbly End Point", secret: true, icon: Key },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Pabbly Connect</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure your Pabbly API credentials for webhook / automation integration.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" />
            Pabbly Credentials
          </CardTitle>
          <CardDescription>
            API Key and Access Token are sent to the Pabbly endpoint on save.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {FIELDS.map(({ key, label, placeholder, secret, icon: Icon }) => {
            const err = errors[key];
            return (
              <div key={key} className="space-y-2">
                <Label>
                  {label} <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                    className={`pl-9 ${err ? "border-destructive" : ""}`}
                  />
                </div>
                {err && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {err}
                  </p>
                )}
              </div>
            );
          })}

          {/* Unsaved changes warning */}
          {hasChanges() && (
            <Alert className="border-warning/30 bg-warning/5">
              <AlertCircle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-warning text-sm">
                You have unsaved changes.
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setForm({ ...original }); setErrors({}); }}
              disabled={!hasChanges() || loading.save}
              className="gap-1"
            >
              <X className="w-3 h-3" /> Cancel
            </Button>

            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges() || loading.save}
              className="gap-1 gradient-primary text-primary-foreground hover:opacity-90"
            >
              {loading.save ? "Saving…" : <><Save className="w-3 h-3" /> Save</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}